import React, { useEffect, useRef } from "react";
import Plotly from "plotly.js-dist-min";
import type { Activity, AlertLevel, AlertProfile } from "../apiSlice";
import { colorForIndex, type SeriesPoint } from "../chartUtils";
import dayjs from "dayjs";

export interface NamedSeries {
  name: string;
  deviceName?: string;
  points: SeriesPoint[];
  alertProfileId: number | null;
}

/**
 * Maps an alert level to its display y-value for the current type/mode.
 * Return `null` to skip a level. `levels` is the full level array of the group
 * (needed for ground-water derived levels).
 */
export type AlertToDisplay = (
  level: AlertLevel,
  levels: AlertLevel[],
) => number | null;

interface LineChartProps {
  series: NamedSeries[];
  yAxisLabel: string;
  yAxisUnit: string;
  hoverFormat: string;
  markersOnly?: boolean;
  invertY?: boolean;
  signNote?: string;
  alertProfiles: Map<number, AlertProfile>;
  alertToDisplay?: AlertToDisplay;
  activities: Activity[];
  startDate: string;
  endDate: string;
  height?: number;
  /** Optional fixed y-axis range [min, max] */
  yAxisRange?: [number, number];
  /** Show minor gridlines for every day */
  showDailyGridlines?: boolean;
  /** Draw each alert line at both +value and -value (movement) */
  mirrorAlertLines?: boolean;
}

const ALERT_COLORS = ["#FFA500", "#FF6347", "#FF0000", "#8B0000", "#4B0000"];
const ALERT_DASHES = ["6px,12px", "6px,8px", "6px,6px", "6px,3px", "solid"];

const ALERT_COLORS_GROUND_WATER = ["#FFA500", "#4B0000"];
const ALERT_DASHES_GROUND_WATER = ["6px,12px", "solid"];

function getAlertTraceStyle(level: AlertLevel, isGroundWaterChart: boolean) {
  if (isGroundWaterChart) {
    const isHighestOrLowest = /^(highest|lowest)$/i.test(level.name);
    const styleIndex = isHighestOrLowest ? 1 : 0;
    return {
      color: ALERT_COLORS_GROUND_WATER[styleIndex],
      dash: ALERT_DASHES_GROUND_WATER[styleIndex],
    };
  }

  return {
    color: ALERT_COLORS[(level.order - 1) % ALERT_COLORS.length],
    dash: ALERT_DASHES[(level.order - 1) % ALERT_DASHES.length],
  };
}

function resolveLevels(
  profile: AlertProfile | undefined,
): AlertLevel[] | undefined {
  if (!profile?.alert_level_json) return undefined;
  const json = profile.alert_level_json;
  if (Array.isArray(json)) return json;
  if (typeof json === "object")
    return (json as { [k: string]: AlertLevel[] }).tran_rs;
  return undefined;
}

const LineChart: React.FC<LineChartProps> = ({
  series,
  yAxisLabel,
  yAxisUnit,
  hoverFormat,
  markersOnly,
  invertY,
  signNote,
  alertProfiles,
  alertToDisplay,
  activities,
  startDate,
  endDate,
  height = 500,
  yAxisRange,
  showDailyGridlines = false,
  mirrorAlertLines = false,
}) => {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const mode = markersOnly ? "markers" : "lines+markers";

    // ---- data traces ----
    const dataTraces = series.map((s, index) => ({
      //no time needed in date, just the date part
      x: s.points.map((p) => dayjs(p.date).format("YYYY-MM-DD")),
      y: s.points.map((p) => p.value),
      type: "scatter" as const,
      mode,
      name: s.name,
      line: { color: colorForIndex(index), width: 2 },
      marker: { size: 4, color: colorForIndex(index) },
      hovertemplate:
        `<b>%{x}</b><br><b>${s.name}</b><br>` +
        `${yAxisLabel}: %{y:${hoverFormat}} ${yAxisUnit}<extra></extra>`,
    }));

    const allValues = series.flatMap((s) => s.points.map((p) => p.value));

    // ---- alert lines ----
    const alertTraces: any[] = [];
    const alertDisplayValues: number[] = [];
    const usedAlertKeys = new Set<string>();
    const isGroundWaterChart = /ground water/i.test(yAxisLabel);
    if (alertToDisplay) {
      series.forEach((s) => {
        if (s.alertProfileId == null) return;
        const profile = alertProfiles.get(s.alertProfileId);
        const levels = resolveLevels(profile);
        if (!levels) return;
        levels.forEach((level) => {
          const displayValue = alertToDisplay(level, levels);
          if (displayValue == null || !Number.isFinite(displayValue)) return;
          const signedValues =
            mirrorAlertLines && displayValue !== 0
              ? [displayValue, -displayValue]
              : [displayValue];
          signedValues.forEach((sv) => {
            const key = `${level.name}_${sv}`;
            if (usedAlertKeys.has(key)) return;
            usedAlertKeys.add(key);

            let gradientText = "";
            if (level.entry_mode === "gradient" && level.value > 0) {
              const n = Math.round(1 / level.value);
              if (n > 0) gradientText = ` 1/${n}`;
            }
            alertDisplayValues.push(sv);
            alertTraces.push({
              x: [
                dayjs(startDate).format("YYYY-MM-DD"),
                dayjs(endDate).format("YYYY-MM-DD"),
              ],
              y: [sv, sv],
              type: "scatter" as const,
              mode: "lines" as const,
              line: {
                color: getAlertTraceStyle(level, isGroundWaterChart).color,
                width: 2,
                dash: getAlertTraceStyle(level, isGroundWaterChart).dash,
              },
              name: `${level.name} (${sv}${yAxisUnit}${gradientText})`,
              showlegend: true,
              hovertemplate: `<b>${level.name}</b><br>${yAxisLabel}: ${sv} ${yAxisUnit}${gradientText}<extra></extra>`,
            });
          });
        });
      });
    }

    // ---- activities timeline (yaxis2 strip) ----
    const chartStart = new Date(startDate);
    const chartEnd = new Date(endDate);
    const deviceNames = new Set(series.map((s) => s.deviceName ?? s.name));
    const relevant = activities.filter((a) => {
      const devs = Array.isArray(a.devices) ? a.devices : [];
      if (devs.length === 0) return true;
      return devs.some((d) => deviceNames.has(d.device_name));
    });
    const visible = relevant.filter((a) => {
      const s = new Date(a.from_date);
      const e = new Date(a.to_date);
      return !(e < chartStart || s > chartEnd);
    });

    // greedy lane assignment
    const sortedAct = [...visible].sort(
      (a, b) =>
        new Date(a.from_date).getTime() - new Date(b.from_date).getTime(),
    );
    const laneLastEnd: number[] = [];
    const activityLane = new Map<number, number>();
    sortedAct.forEach((a) => {
      const s = new Date(a.from_date).getTime();
      const e = new Date(a.to_date).getTime();
      let lane = laneLastEnd.findIndex((end) => end < s);
      if (lane === -1) {
        lane = laneLastEnd.length;
        laneLastEnd.push(e);
      } else {
        laneLastEnd[lane] = e;
      }
      activityLane.set(a.id, lane);
    });
    const laneCount = Math.max(1, laneLastEnd.length);

    const eventTraces: any[] = [];
    visible.forEach((activity) => {
      const rawStart = new Date(activity.from_date);
      const rawEnd = new Date(activity.to_date);
      const aStart = rawStart < chartStart ? new Date(chartStart) : rawStart;
      const aEnd = rawEnd > chartEnd ? new Date(chartEnd) : rawEnd;
      const lane = activityLane.get(activity.id) ?? 0;
      const slotTop = 1 - lane / laneCount;
      const slotBottom = 1 - (lane + 1) / laneCount;
      const barY = slotBottom + (slotTop - slotBottom) * 0.35;
      const labelY = slotBottom + (slotTop - slotBottom) * 0.78;
      const isRange = aEnd.getTime() !== aStart.getTime();

      if (isRange) {
        eventTraces.push({
          x: [aStart, aEnd],
          y: [barY, barY],
          yaxis: "y2",
          type: "scatter" as const,
          mode: "lines+markers" as const,
          line: { color: "#ff6b6b", width: 4 },
          marker: { size: 8, color: "#ff6b6b" },
          name: activity.activity_name,
          showlegend: false,
          hovertemplate:
            `<b>${activity.activity_name}</b><br>` +
            (activity.notes ? `${activity.notes}<br>` : "") +
            `From: ${rawStart.toLocaleDateString()}<br>` +
            `To: ${rawEnd.toLocaleDateString()}<extra></extra>`,
        });
      } else {
        eventTraces.push({
          x: [aStart, aStart],
          y: [slotBottom, slotTop],
          yaxis: "y2",
          type: "scatter" as const,
          mode: "lines" as const,
          line: { color: "#ff6b6b", width: 2, dash: "dash" },
          name: activity.activity_name,
          showlegend: false,
          hovertemplate:
            `<b>${activity.activity_name}</b><br>` +
            (activity.notes ? `${activity.notes}<br>` : "") +
            `Date: ${aStart.toLocaleDateString()}<extra></extra>`,
        });
      }
      eventTraces.push({
        x: [
          isRange ? new Date((aStart.getTime() + aEnd.getTime()) / 2) : aStart,
        ],
        y: [labelY],
        yaxis: "y2",
        type: "scatter" as const,
        mode: "text" as const,
        text: [activity.activity_name],
        textposition: "middle center",
        textfont: { size: 11, color: "#ff6b6b" },
        showlegend: false,
        hoverinfo: "skip",
      });
    });

    // ---- y-axis range ----
    let lo: number, hi: number;
    if (yAxisRange) {
      [lo, hi] = yAxisRange;
    } else {
      const combined = [...allValues, ...alertDisplayValues];
      const yMin = combined.length ? Math.min(...combined) : -50;
      const yMax = combined.length ? Math.max(...combined) : 50;
      const yRange = yMax - yMin || 1;
      lo = yMin - yRange * 0.1;
      hi = yMax + yRange * 0.15;
    }

    const hasActivities = eventTraces.length > 0;
    const stripBottomGap = 0.06;
    const perLaneHeight = 0.07;
    const stripHeight = hasActivities
      ? Math.min(0.45, perLaneHeight * laneCount + 0.03)
      : 0;
    const stripTop = stripBottomGap + stripHeight;
    const mainYDomain: [number, number] = hasActivities
      ? [stripTop + 0.04, 1]
      : [0, 1];

    const layout: any = {
      xaxis: {
        title: { text: "Date" },
        type: "date",
        tickangle: -90,
        tickformat: "%Y-%m-%d",
        fixedrange: true,
        // Spike line gives a date-reference crosshair even in "closest" hover mode.
        showspikes: true,
        spikemode: "across",
        spikedash: "solid",
        spikecolor: "rgba(100,100,100,0.35)",
        spikethickness: 1,
        ...(showDailyGridlines
          ? {
              minor: {
                dtick: 86400000,
                showgrid: true,
                gridcolor: "rgba(128,128,128,0.15)",
                gridwidth: 0.5,
              },
            }
          : {}),
      },
      yaxis: {
        title: { text: yAxisLabel },
        tickformat: hoverFormat,
        fixedrange: true,
        domain: mainYDomain,
        range: invertY ? [hi, lo] : [lo, hi],
        autorange: false,
      },
      yaxis2: {
        domain: [stripBottomGap, stripTop],
        range: [0, 1],
        fixedrange: true,
        showgrid: false,
        zeroline: false,
        showticklabels: false,
        visible: hasActivities,
      },
      height,
      // "closest" shows one tooltip at a time; cleaner with many checkpoints.
      hovermode: "closest",
      showlegend: true,
      legend: {
        x: 1.02,
        y: 1,
        bgcolor: "rgba(255,255,255,0.85)",
        bordercolor: "rgba(0,0,0,0.15)",
        borderwidth: 1,
      },
      dragmode: false,
      margin: { l: 70, r: 160, t: 20, b: 80 },
    };

    const config = {
      responsive: true,
      displayModeBar: true,
      modeBarButtonsToRemove: [
        "pan2d",
        "lasso2d",
        "select2d",
        "zoom2d",
        "autoScale2d",
      ],
      displaylogo: false,
    };

    Plotly.newPlot(
      el,
      [
        ...dataTraces,
        ...alertTraces,
        ...eventTraces.map((evt) => ({
          ...evt,
          x: evt.x.map((d: Date) => dayjs(d).format("YYYY-MM-DD")),
        })),
      ],
      layout,
      config,
    );

    return () => {
      Plotly.purge(el);
    };
  }, [
    series,
    yAxisLabel,
    yAxisUnit,
    hoverFormat,
    markersOnly,
    invertY,
    alertProfiles,
    alertToDisplay,
    activities,
    startDate,
    endDate,
    height,
    yAxisRange,
    showDailyGridlines,
    mirrorAlertLines,
  ]);

  return (
    <div>
      <div ref={ref} style={{ width: "100%" }} />
      {signNote && (
        <div className="text-muted text-sm" style={{ padding: "6px 4px" }}>
          {signNote}
        </div>
      )}
    </div>
  );
};

export default LineChart;
